// frontend/src/components/admin/tenants/ouTree/OrphansBranchNode.tsx
/**
 * Card de "Sem OU mapeada" — agrupa tenants legados ou orfaos no topo
 * da arvore. Nao e uma OU AWS real, apenas um container visual com a
 * mesma estetica das UO pai (cards com env pills).
 */
import { useState, useCallback } from 'react'
import type { KeyboardEvent } from 'react'
import {
  Card,
  Group,
  Text,
  Badge,
  Box,
  Collapse,
  Tooltip,
} from '@mantine/core'
import { IconChevronRight, IconAlertTriangle } from '@tabler/icons-react'
import { EnvironmentRow } from './EnvironmentRow'
import { colorForEnv } from './envOrdering'
import type { OuTreeOrphanRoot } from './types'
import type { TenantRow } from '../TenantsTable'

interface RowProps {
  onToggleField: (id: number, field: string, value: boolean) => void
  onToggleZabbix: (tenantId: number, enabled: boolean) => void
  onToggleGitlab: (tenantId: number, currentlyEnabled: boolean) => void
  onToggleWhatsApi: (tenantId: number, enabled: boolean) => void
  onUpdateTimezone?: (tenantId: number, timezone: string | null) => void
  onEditRegions?: (tenant: TenantRow) => void
  onProvisioningFailedClick?: (tenant: TenantRow) => void
  fieldPendingIds?: Set<number>
  whatsapiPendingIds?: Set<number>
  timezonePendingIds?: Set<number>
}

interface Props {
  branch: OuTreeOrphanRoot
  rowProps: RowProps
}

export function OrphansBranchNode({ branch, rowProps }: Props) {
  const [expanded, setExpanded] = useState(true)
  const count = branch.children.length

  const handleToggle = useCallback(() => {
    setExpanded((v) => !v)
  }, [])

  const handleKey = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setExpanded((v) => !v)
    }
  }, [])

  return (
    <Card
      withBorder
      radius="md"
      p={0}
      data-testid="orphan-branch"
      style={{ overflow: 'hidden' }}
    >
      <Box
        px="md"
        py="md"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Recolher' : 'Expandir'} Sem OU mapeada`}
        onKeyDown={handleKey}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background 120ms',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--mantine-color-default-hover)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent'
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="md" wrap="nowrap" align="center" style={{ flex: 1, minWidth: 0 }}>
            <IconChevronRight
              size={16}
              style={{
                transition: 'transform 200ms',
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                color: 'var(--mantine-color-dimmed)',
                flexShrink: 0,
              }}
            />
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Group gap="xs" align="baseline" wrap="wrap">
                <Text fw={700} size="md" style={{ letterSpacing: -0.2 }}>
                  Sem OU mapeada
                </Text>
                <Tooltip
                  label="Contas que não estão mapeadas para nenhuma OU do AWS Organizations. Possível legado."
                  withArrow
                  multiline
                  maw={300}
                >
                  <Badge
                    size="xs"
                    color="yellow"
                    variant="light"
                    leftSection={<IconAlertTriangle size={10} />}
                    style={{ textTransform: 'none', cursor: 'help' }}
                  >
                    legado
                  </Badge>
                </Tooltip>
              </Group>
              <Group gap={6} mt={2} wrap="wrap">
                <Text size="xs" c="dimmed">
                  {count} {count === 1 ? 'conta' : 'contas'}
                </Text>
              </Group>
            </Box>
          </Group>
        </Group>
      </Box>

      <Collapse in={expanded}>
        <Box
          style={{
            borderTop: '1px solid var(--mantine-color-default-border)',
          }}
        >
          {branch.children.map((c, idx) => {
            const envName = (c.tenant.environment || '?').toUpperCase()
            const envColor = colorForEnv(c.tenant.environment)
            return (
              <EnvironmentRow
                key={`orphan-${c.tenant.id}`}
                envName={envName}
                envColor={envColor}
                tenant={c.tenant}
                showSeparator={idx < branch.children.length - 1}
                {...rowProps}
              />
            )
          })}
        </Box>
      </Collapse>
    </Card>
  )
}
